<script setup>
import { useI18n } from 'vue-i18n';
import { displayReferenceDate } from './postSaleDate';

defineProps({
  documents: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['download']);
const { t } = useI18n();
const typeEntries = document =>
  document.document_types || document.tipo_documento_ids || [];
const documentTypes = document =>
  typeEntries(document)
    .map(type => ({
      id: type.id || type.tipo_documento_id,
      name: type.name || type.nome || type.nome_tipo,
      validityDays: type.validity_days || type.validade_dias,
      issuedOn: type.issued_on || type.data_emissao,
      expiresOn: type.expires_on || type.data_vencimento,
    }))
    .filter(type => type.name);
const documentTypeNames = document =>
  documentTypes(document)
    .map(type => type.name)
    .filter(Boolean)
    .join(', ');
const validityTypes = document =>
  documentTypes(document).filter(type => Boolean(type.validityDays));
const filename = document =>
  document.filename || document.nome_original || document.arquivo || '';
</script>

<template>
  <p v-if="loading" class="text-sm text-lais-text-muted">
    {{ t('SALES_PROPERTY.DOCUMENTS.LOADING') }}
  </p>
  <p v-else-if="!documents.length" class="text-sm text-lais-text-muted">
    {{ t('SALES_PROPERTY.DOCUMENTS.SUMMARY.EMPTY') }}
  </p>
  <div
    v-else
    class="grid gap-x-2 gap-y-5 min-[701px]:grid-cols-2"
    data-test="sales-property-documents-summary"
  >
    <p class="text-xs font-semibold text-lais-text-muted">
      {{ t('SALES_PROPERTY.DOCUMENTS.SUMMARY.TYPE') }}
    </p>
    <p class="text-xs font-semibold text-lais-text-muted">
      {{ t('SALES_PROPERTY.DOCUMENTS.SUMMARY.NAME') }}
    </p>
    <template
      v-for="(document, index) in documents"
      :key="document.id || index"
    >
      <div class="col-span-full border-t border-lais-border-soft" />
      <div
        class="grid min-w-0 gap-2"
        data-testid="sales-property-summary-document"
      >
        <p class="text-sm text-lais-text-body">
          {{ documentTypeNames(document) }}
        </p>
        <div
          v-if="validityTypes(document).length"
          class="grid gap-2 rounded bg-lais-bg-soft p-5 text-xs text-lais-text-muted min-[701px]:w-[200%] min-[701px]:grid-cols-[minmax(0,5fr)_minmax(0,1fr)_max-content_max-content]"
          data-test="sales-property-document-validity"
        >
          <p class="uppercase">
            {{ t('SALES_PROPERTY.DOCUMENTS.SUMMARY.DOCUMENT') }}
          </p>
          <p class="uppercase">
            {{ t('SALES_PROPERTY.DOCUMENTS.SUMMARY.VALIDITY') }}
          </p>
          <p class="uppercase">
            {{ t('SALES_PROPERTY.DOCUMENTS.SUMMARY.ISSUED_ON') }}
          </p>
          <p class="uppercase">
            {{ t('SALES_PROPERTY.DOCUMENTS.SUMMARY.EXPIRES_ON') }}
          </p>
          <template v-for="type in validityTypes(document)" :key="type.id">
            <span
              class="w-fit max-w-full truncate rounded-full bg-white px-2.5 py-1 text-lais-text-body"
            >
              {{ type.name }}
            </span>
            <span
              class="w-fit rounded-full bg-white px-2.5 py-1 text-lais-text-body"
            >
              {{
                t('SALES_PROPERTY.DOCUMENTS.SUMMARY.DAYS', {
                  count: type.validityDays,
                })
              }}
            </span>
            <span
              class="w-fit rounded-full bg-white px-2.5 py-1 text-lais-text-body"
            >
              {{
                displayReferenceDate(type.issuedOn) ||
                t('SALES_PROPERTY.DOCUMENTS.NO_ISSUED_ON')
              }}
            </span>
            <span
              class="w-fit rounded-full bg-white px-2.5 py-1 text-lais-text-body"
            >
              {{
                displayReferenceDate(type.expiresOn) ||
                t('SALES_PROPERTY.DOCUMENTS.NO_EXPIRES_ON')
              }}
            </span>
          </template>
        </div>
      </div>
      <div class="grid min-w-0 content-start">
        <button
          v-if="document.download_available"
          type="button"
          class="w-fit max-w-full truncate text-left text-sm font-semibold text-lais-primary hover:underline focus-visible:shadow-lais-focus"
          data-test="sales-property-summary-download"
          @click="emit('download', document)"
        >
          {{ filename(document) }}
        </button>
        <p v-else class="truncate text-sm text-lais-text-body">
          {{ filename(document) }}
        </p>
      </div>
    </template>
  </div>
</template>
